import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

const TagsInput = ({
                       name,
                       title,
                       value = [],
                       options = [],
                       onChange
                   }) => {

    const tags = Array.isArray(value) ? value : [];

    const [inputValue, setInputValue] = useState("");
    const [isOptionsVisible, setIsOptionsVisible] = useState(false);

    const containerRef = useRef(null);
    const inputRef = useRef(null);


    /*
    -------------------------------------------------
    CLOSE OPTIONS WHEN CLICKING OUTSIDE
    -------------------------------------------------
    */

    useEffect(() => {

        const handleClickOutside = (e) => {

            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setIsOptionsVisible(false);
            }

        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };

    }, []);


    /*
    -------------------------------------------------
    ADD TAG
    -------------------------------------------------
    */

    const addTag = (tag) => {

        const cleanTag = String(tag).trim();

        if (!cleanTag) {
            return;
        }

        if (tags.includes(cleanTag)) {
            setInputValue("");
            return;
        }

        const updatedTags = [
            ...tags,
            cleanTag
        ];

        if (onChange) {
            onChange(updatedTags);
        }

        setInputValue("");
    };


    /*
    -------------------------------------------------
    REMOVE TAG
    -------------------------------------------------
    */

    const removeTag = (index) => {

        const updatedTags = tags.filter(
            (_, i) => i !== index
        );

        if (onChange) {
            onChange(updatedTags);
        }

    };


    /*
    -------------------------------------------------
    KEYBOARD
    -------------------------------------------------
    */

    const handleKeyDown = (e) => {

        if (
            e.key === "Enter" ||
            e.key === ","
        ) {

            e.preventDefault();

            addTag(inputValue);

            return;
        }


        /*
        Remove last tag with Backspace
        when input is empty.
        */

        if (
            e.key === "Backspace" &&
            inputValue === "" &&
            tags.length > 0
        ) {

            removeTag(tags.length - 1);
        }

    };


    /*
    -------------------------------------------------
    OPTION SELECTION
    -------------------------------------------------
    */

    const handleOptionClick = (option) => {

        addTag(option);

        setIsOptionsVisible(false);

        inputRef.current?.focus();
    };


    /*
    -------------------------------------------------
    FILTER OPTIONS
    -------------------------------------------------
    */

    const filteredOptions = options.filter(
        option =>
            !tags.includes(option) &&
            String(option)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
    );


    return (

        <div
            ref={containerRef}
            className="mb-4"
        >

            <div
                className="
                    flex flex-wrap
                    items-center
                    gap-2
                    bg-light-bg
                    border-2
                    border-gray-200
                    rounded-md
                    p-2
                    rtl
                "
            >

                {
                    tags.map((tag, index) => (

                        <div
                            key={`${tag}-${index}`}
                            className="
                                flex
                                items-center
                                bg-gray-300
                                text-black
                                px-2
                                py-2
                                rounded-sm
                                gap-x-2
                            "
                        >

                            <span className="relative">
                                {tag}
                            </span>

                            <button
                                type="button"
                                className="
                                    mr-3
                                    p-1
                                    rounded-sm
                                    bg-white
                                    text-black
                                    flex
                                    relative
                                    hover:text-red-500
                                "
                                onClick={() =>
                                    removeTag(index)
                                }
                            >

                                <FontAwesomeIcon
                                    icon={faClose}
                                />

                            </button>

                        </div>

                    ))}


                <input
                    ref={inputRef}
                    name={name}
                    type="text"
                    value={inputValue}
                    className="
                        border-none
                        focus:outline-none
                        p-2
                        flex-1
                        min-w-[120px]
                        bg-transparent
                    "
                    placeholder={title}
                    onFocus={() =>
                        setIsOptionsVisible(true)
                    }
                    onChange={(e) =>
                        setInputValue(e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                />

            </div>


            {
                isOptionsVisible &&
                filteredOptions.length > 0 && (

                    <div
                        className="
                            flex
                            flex-col
                            items-stretch
                            gap-1
                            p-2
                            bg-gray-200
                            rounded-md
                            mt-1
                            max-h-48
                            overflow-y-auto
                        "
                    >

                        {
                            filteredOptions.map(
                                (option, index) => (

                                    <button
                                        type="button"
                                        className="
                                            px-5
                                            py-2
                                            rounded-md
                                            text-md
                                            text-right
                                            transition
                                            hover:bg-gray-300
                                        "
                                        key={`${option}-${index}`}
                                        onClick={() =>
                                            handleOptionClick(
                                                option
                                            )
                                        }
                                    >
                                        {option}
                                    </button>

                                )
                            )
                        }

                    </div>

                )
            }

        </div>

    );

};

export default TagsInput;